import { Localized, useLocalization } from "@fluent/react"
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import { Box, Checkbox, Dialog, Fab, IconButton, Paper, TableContainer, Tooltip } from "@mui/material"
import { unwrapResult } from "@reduxjs/toolkit"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import produce from "immer"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { APIError } from "../../../app/appTypes"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { selectSettings } from "../../settings/settingsSlice"
import { activateCompanySSOSetting, deactivateCompanySSOSetting, loadCompanySSOSettings, SysCompanyTenant } from "./CompanySSOApi"
import { CreateEditCompanySSOSettingsDialog } from "./CreateEditCompanySSOSettingsDialog"
import { DeleteCompanySSOSettingsDialog } from "./DeleteCompanySSOSettingsDialog"

interface CompanySSOSettingsProps {
    companyId: string;
}

type DialogState =
    | { type: "none" }
    | { type: "createEdit", companyTenant: SysCompanyTenant | undefined }
    | { type: "delete", companyTenantId: number }

export const CompanySSOSettings = ({ companyId }: CompanySSOSettingsProps) => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const dispatch = useAppDispatch()

    const [rows, setRows] = useState<SysCompanyTenant[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState<APIError>()
    const [dialog, setDialog] = useState<DialogState>({ type: "none" })

    useEffect(() => {
        if (!companyId) {
            setRows([])
            return
        }

        setIsLoading(true)
        setIsError(false)
        setError(undefined)

        dispatch(loadCompanySSOSettings(companyId))
            .then(unwrapResult)
            .then(setRows)
            .catch((loadError: APIError) => {
                setIsError(true)
                setError(loadError)
            })
            .finally(() => setIsLoading(false))
    }, [companyId, dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText
        } else {
            return defaultEnLocaleText
        }
    }

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback((companyTenant: SysCompanyTenant) => {
            setter({ type: "none" })
            setRows(
                produce((draft) => {
                    if (draft !== undefined) {
                        const existingIndex = draft.findIndex((item) => item.id === companyTenant.id)

                        if (existingIndex !== -1) {
                            draft[existingIndex] = companyTenant
                        } else {
                            draft.push(companyTenant)
                        }
                    }
                })
            )
        }, [setter])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "createEdit":
                return <CreateEditCompanySSOSettingsDialog
                    mode={state.companyTenant ? "edit" : "create"}
                    companyId={companyId}
                    companyTenant={state.companyTenant}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />

            case "delete":
                return <DeleteCompanySSOSettingsDialog
                    companyTenantId={state.companyTenantId}
                    onDeleteSuccess={(id) => {
                        setter({ type: "none" })
                        setRows(
                            produce((draft) => {
                                if (draft !== undefined) {
                                    const existingIndex = draft.findIndex((item) => item.id === id)

                                    if (existingIndex !== -1) {
                                        draft.splice(existingIndex, 1)
                                    }
                                }
                            })
                        )
                    }}
                    onCancel={onCancel}
                />
        }
    }

    const handleAddCompanySSOSettingClick = useCallback(() => {
        setDialog({
            type: "createEdit",
            companyTenant: undefined,
        })
    }, [])

    const handleEditCompanySSOSettingClick = useCallback((companyTenant: SysCompanyTenant) => {
        setDialog({
            type: "createEdit",
            companyTenant,
        })
    }, [])

    const handleDeleteCompanySSOSettingClick = useCallback((companyTenantId: number) => {
        setDialog({
            type: "delete",
            companyTenantId,
        })
    }, [])

    const handleCompanySSOSettingStateChange = useCallback(async (companyTenant: SysCompanyTenant) => {
        try {
            const result = companyTenant.isActive
                ? await dispatch(deactivateCompanySSOSetting(companyTenant.id))
                : await dispatch(activateCompanySSOSetting(companyTenant.id))

            if (activateCompanySSOSetting.fulfilled.match(result) || deactivateCompanySSOSetting.fulfilled.match(result)) {
                const updatedCompanyTenant = result.payload as SysCompanyTenant

                setRows(
                    produce((draft) => {
                        if (draft !== undefined) {
                            const existingIndex = draft.findIndex((item) => item.id === updatedCompanyTenant.id)

                            if (existingIndex !== -1) {
                                draft[existingIndex] = updatedCompanyTenant
                            }
                        }
                    })
                )

                dispatch(showSuccess("saved"))
            } else {
                dispatch(showError("error"))
            }
        }
        catch {
            dispatch(showError("error"))
        }
    }, [dispatch])

    const columns: GridColDef<SysCompanyTenant>[] = [
        {
            field: 'provider',
            headerName: l10n.getString("company-sso-provider", null, "Provider"),
            flex: 1,
            minWidth: 140,
        },
        {
            field: 'tenantId',
            headerName: l10n.getString("company-sso-tenant-id", null, "Tenant ID"),
            flex: 1,
            minWidth: 180,
        },
        {
            field: 'tenantName',
            headerName: l10n.getString("company-sso-tenant-name", null, "Tenant name"),
            flex: 1,
            minWidth: 180,
        },
        {
            field: 'defaultDomain',
            headerName: l10n.getString("company-sso-default-domain", null, "Default domain"),
            flex: 1,
            minWidth: 180,
        },
        {
            field: 'isActive',
            headerName: l10n.getString("company-sso-is-active", null, "Active"),
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => <Checkbox disabled checked={params.row.isActive} />,
        },
        {
            field: 'allowSelfRegistration',
            headerName: l10n.getString("company-sso-allow-self-registration", null, "Self registration"),
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => <Checkbox disabled checked={params.row.allowSelfRegistration} />,
        },
        {
            field: 'allowExternalUsers',
            headerName: l10n.getString("company-sso-allow-external-users", null, "External users"),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => <Checkbox disabled checked={params.row.allowExternalUsers} />,
        },
        {
            field: 'actions',
            type: 'actions',
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <>
                    <Tooltip title={l10n.getString("edit", null, "Edit")}>
                        <IconButton color="secondary" onClick={() => handleEditCompanySSOSettingClick(params.row)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={params.row.isActive ? l10n.getString("structure-deactivate", null, "Deactivate") : l10n.getString("structure-activate", null, "Activate")}>
                        <IconButton color={params.row.isActive ? "warning" : "success"} onClick={() => handleCompanySSOSettingStateChange(params.row)}>
                            {params.row.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={l10n.getString("delete", null, "Delete")}>
                        <IconButton color="error" onClick={() => handleDeleteCompanySSOSettingClick(params.row.id)}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ]

    if (isLoading) {
        return <LoadingContainer />
    }

    if (isError) {
        return <ErrorContainer error={error} />
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid
                    localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 525px)' }}
                    rows={rows}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'provider', sort: 'asc' }] }
                    }}
                    disableRowSelectionOnClick
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Fab sx={{
                        margin: 1,
                    }}
                    onClick={handleAddCompanySSOSettingClick} color="secondary" size="small" >
                        <AddIcon />
                    </Fab>
                </Box>
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="sm" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}